import {ErrorStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useGetServiceOrderTypesQuery,
  useGetSettingsServiceOrderVariantsAllQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import type {useWorkshopDashboardFilters} from '../hooks/useWorkshopDashboardFilters';
import {
  WorkshopOverviewFilters,
  type WorkshopOverviewFiltersProps,
} from './WorkshopOverviewFilters';

interface WorkshopOverviewFilterOptionsProps
  extends Pick<WorkshopOverviewFiltersProps, 'filters' | 'onClearFilters'> {
  onFilterChange: ReturnType<typeof useWorkshopDashboardFilters>['onFilterChange'];
}

export function WorkshopOverviewFilterOptions(props: WorkshopOverviewFilterOptionsProps) {
  const user = useGetCurrentUserInfoQuery();
  const types = useGetServiceOrderTypesQuery();
  const variants = useGetSettingsServiceOrderVariantsAllQuery();

  const permittedBranchIds = user.data?.branches?.map((branch) => branch.id) ?? [];
  const branchOptions =
    user.data?.branches?.map((branch) => ({label: branch.name, value: branch.id})) ?? [];
  const activeBranchIds =
    props.filters.branchId.length > 0 ? props.filters.branchId : permittedBranchIds;
  const orderTypeOptions =
    types.data?.flatMap((type) =>
      isNotNil(type?.id) && isNotNil(type?.name) && isNotNil(type?.code)
        ? [{label: type.name, value: type.id}]
        : []
    ) ?? [];
  const variantOptions =
    variants.data
      ?.filter(isNotNil)
      .filter((variant) => isNil(variant.branchId) || permittedBranchIds.includes(variant.branchId))
      .filter(
        (variant) =>
          isNil(variant.branchId) ||
          activeBranchIds.includes(variant.branchId) ||
          props.filters.serviceOrderVariantId.includes(variant.id)
      )
      .map((variant) => ({label: variant.name, value: variant.id})) ?? [];

  const onFilterChange: WorkshopOverviewFiltersProps['onFilterChange'] = (key, values) => {
    const orderTypes =
      types.data?.flatMap((type) =>
        isNotNil(type?.id) && isNotNil(type?.code) && values?.includes(type.id)
          ? [{id: type.id, code: type.code}]
          : []
      ) ?? [];
    props.onFilterChange(key, values, orderTypes);
  };

  const isError = user.isError || types.isError || variants.isError;
  const onRetry = () => {
    user.refetch();
    types.refetch();
    variants.refetch();
  };

  return (
    <VStack spacing={3}>
      <WorkshopOverviewFilters
        {...props}
        onFilterChange={onFilterChange}
        branchOptions={branchOptions}
        orderTypeOptions={orderTypeOptions}
        variantOptions={variantOptions}
        isLoading={user.isLoading || types.isLoading || variants.isLoading}
        isError={isError}
      />
      <Show when={isError}>
        <ErrorStatus
          headline={i18n.t('general.errors.cannotLoadOptions')}
          action={{title: i18n.t('general.actions.retry'), onClick: onRetry}}
        />
      </Show>
    </VStack>
  );
}
